import { clampValue } from '@/utils/numbers';
import { EditableInput } from './EditableInput';

const TIME_INPUT_WIDTH = 56;

function formatTime(currentTime: number): string {
  const totalSeconds = Math.max(0, Math.floor(currentTime));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

function parseTime(input: string): number | null {
  const trimmed = input.trim();
  const match = trimmed.match(/^(\d+)(?::([0-5]?\d))?$/);

  if (!match) return null;

  const firstSegment = Number(match[1]);
  const secondSegment = match[2] === undefined ? undefined : Number(match[2]);

  return secondSegment === undefined ? firstSegment : firstSegment * 60 + secondSegment;
}

interface TimeInputProps {
  currentTime: number;
  duration: number;
  paused: boolean;
  onTogglePlay: () => void;
  onSetTime: (time: number, ongoing: boolean) => void;
}

export function TimeInput({ currentTime, duration, paused, onTogglePlay, onSetTime }: TimeInputProps) {
  const syncedValue = formatTime(currentTime);

  const commit = (value: string): string | null => {
    const parsed = parseTime(value);
    if (parsed === null) return null;

    const nextTime = clampValue(parsed, 0, duration);
    onSetTime(nextTime, false);
    return formatTime(nextTime);
  };

  return (
    <EditableInput
      ariaLabel="Playback time"
      syncedValue={syncedValue}
      width={TIME_INPUT_WIDTH}
      paused={paused}
      onTogglePlay={onTogglePlay}
      onCommit={commit}
    />
  );
}
